import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PosterOne from "../share/component.jsx/posterone";
import aboutbanner from "../assets/aboutbanner.jpg";
import BannerOne from "../share/component.jsx/banner";


/* ─── FOREST PLOT VISUAL ─────────────────────────────── */
const ForestPlot = () => {
  const studies = [
    { label: "Smith et al. 2019", n: "n=1,240", effect: 0.62, ci: [0.41, 0.83], weight: 18 },
    { label: "Chen et al. 2020", n: "n=890", effect: 0.78, ci: [0.55, 1.01], weight: 14 },
    { label: "Patel et al. 2021", n: "n=2,100", effect: 0.45, ci: [0.30, 0.60], weight: 22 },
    { label: "Kumar et al. 2021", n: "n=670", effect: 0.91, ci: [0.63, 1.19], weight: 11 },
    { label: "Wang et al. 2022", n: "n=3,450", effect: 0.55, ci: [0.44, 0.66], weight: 28 },
    { label: "POOLED ESTIMATE", n: "N=8,350", effect: 0.60, ci: [0.51, 0.69], weight: null, isPool: true },
  ];

  const [animated, setAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setAnimated(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const scale = (v) => ((v / 1.4) * 100).toFixed(1) + "%";

  return (
    <div ref={ref} style={{ background: "#0d1b2a", borderRadius: 20, padding: "32px 28px", fontFamily: "'IBM Plex Mono', monospace" }}>
      <div style={{ fontSize: 10, color: "#4a9eca", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 20 }}>
        Forest Plot — Pooled Effect Size (OR)
      </div>

      {/* axis */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, paddingLeft: 140 }}>
        {["0.2", "0.4", "0.6", "0.8", "1.0", "1.2", "1.4"].map(v => (
          <span key={v} style={{ fontSize: 9, color: "#2d4a5f", minWidth: 0 }}>{v}</span>
        ))}
      </div>

      {studies.map((s, i) => {
        const isPool = s.isPool;
        const barLeft = scale(s.ci[0]);
        const barRight = scale(s.ci[1]);
        const dotPos = scale(s.effect);
        const delay = i * 0.12;

        return (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: isPool ? 0 : 8, marginTop: isPool ? 12 : 0, paddingTop: isPool ? 12 : 0, borderTop: isPool ? "1px solid #1e3a4f" : "none" }}>
            {/* label */}
            <div style={{ width: 140, flexShrink: 0 }}>
              <div style={{ fontSize: isPool ? 9.5 : 9, color: isPool ? "#4a9eca" : "#6b8ea8", fontWeight: isPool ? 700 : 400, letterSpacing: "0.02em" }}>{s.label}</div>
              <div style={{ fontSize: 8, color: "#2d4a5f" }}>{s.n}{s.weight ? ` · ${s.weight}%` : ""}</div>
            </div>

            {/* plot area */}
            <div style={{ flex: 1, height: isPool ? 20 : 16, position: "relative" }}>
              {/* vertical null line */}
              <div style={{ position: "absolute", left: scale(1.0), top: 0, bottom: 0, width: 1, background: "#1e3a4f" }} />

              {/* CI bar */}
              <div style={{
                position: "absolute",
                left: barLeft,
                right: `${100 - parseFloat(scale(s.ci[1]))}%`,
                top: "50%",
                height: isPool ? 3 : 2,
                transform: "translateY(-50%)",
                background: isPool ? "#4a9eca" : "#2d6a8f",
                borderRadius: 2,
                transition: animated ? `width ${0.6 + delay}s ease` : "none",
                opacity: animated ? 1 : 0,
              }} />

              {/* diamond or dot */}
              {isPool ? (
                <div style={{
                  position: "absolute",
                  left: `calc(${dotPos} - 10px)`,
                  top: "50%",
                  transform: "translateY(-50%) rotate(45deg)",
                  width: 14, height: 14,
                  background: "#4a9eca",
                  opacity: animated ? 1 : 0,
                  transition: animated ? `opacity ${0.8 + delay}s ease` : "none",
                }} />
              ) : (
                <div style={{
                  position: "absolute",
                  left: `calc(${dotPos} - 5px)`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: s.weight ? Math.max(8, s.weight * 0.4) : 8,
                  height: s.weight ? Math.max(8, s.weight * 0.4) : 8,
                  borderRadius: "50%",
                  background: "#1a7ab5",
                  border: "2px solid #4a9eca",
                  opacity: animated ? 1 : 0,
                  transition: animated ? `opacity ${0.5 + delay}s ease` : "none",
                }} />
              )}
            </div>

            {/* OR value */}
            <div style={{ width: 52, textAlign: "right", fontSize: isPool ? 10 : 9, color: isPool ? "#4a9eca" : "#4a7a9b", fontWeight: isPool ? 700 : 400, flexShrink: 0 }}>
              {s.effect.toFixed(2)}
            </div>
          </div>
        );
      })}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, paddingLeft: 140 }}>
        <span style={{ fontSize: 9, color: "#2d4a5f" }}>Favours Treatment</span>
        <span style={{ fontSize: 9, color: "#2d4a5f" }}>Favours Control</span>
      </div>
    </div>
  );
};

/* ─── DATA ───────────────────────────────────────────── */
const SERVICES = [
  { id: 1, num: "01", image: require("../assets/services/Research_Question_Planning.jpg"), title: "Research Question Planning", desc: "Re-examine the study question and define the precise research focus so evidence is gathered with clarity of direction and purpose.", tags: ["Research Question", "Planning", "Clarity"] },
  { id: 2, num: "02", image: require("../assets/services/Literature_Screening_Support.jpg"), title: "Literature Screening Support", desc: "Identify and screen relevant clinical studies to remove weak or irrelevant data and keep the analysis focused, reliable, and evidence-based.", tags: ["Screening", "PRISMA", "Study Selection"] },
  { id: 3, num: "03", image: require("../assets/services/Data_Extraction_Guidance.jpg"), title: "Data Extraction Guidance", desc: "Collect study details in a consistent format and prepare the dataset for analysis with accuracy, transparency, and structured evidence organization.", tags: ["Extraction", "Data Quality", "Consistency"] },
  { id: 4, num: "04", image: require("../assets/services/Statistical_Pooling_Helps.jpg"), title: "Statistical Pooling Helps", desc: "Combine results using appropriate meta-analytic methods so evidence is pooled correctly, helping you draw stronger conclusions from multiple studies.", tags: ["Pooling", "Forest Plot", "Interpretation"] },
  { id: 5, num: "05", image: require("../assets/services/Publication-Ready_Reporting.jpg"), title: "Publication-Ready Reporting", desc: "Produce manuscript-ready summaries, clear results discussion, journal-friendly reporting and revision support for confident submission.", tags: ["Reporting", "Manuscripts", "Publication"] },
  { id: 6, num: "06", image: require("../assets/services/Tailored_Research_Support.jpg"), title: "Tailored Research Support", desc: "Customized support for medical students, researchers, clinicians, and academic authors who need precise, well-organized evidence-based outcomes.", tags: ["Students", "Clinicians", "Authors"] },
];

const STATS = [
  { value: "300+", label: "Meta-Analyses Completed" },
  { value: "95%", label: "Publication Success Rate" },
  { value: "45+", label: "Journals Published In" },
  { value: "12+", label: "Years Specializing" },
];

const PROCESS = [
  { num: "01", title: "Get the Research Objective", desc: "Re-examine the study question and define the precise research focus before any evidence is collected. This aids in establishing the precise study area and direction for your meta-analysis." },
  { num: "02", title: "Review Existing Research", desc: "Identify and screen relevant studies so weak or irrelevant data is excluded and the analysis remains robust. This eliminates weak or irrelevant data and keeps your analysis focused." },
  { num: "03", title: "Sort the Data", desc: "Gather study details in a clear format to prepare the data for analysis and ensure consistency. This facilitates the analytical process and maintains data integrity." },
  { num: "04", title: "Do the Analysis", desc: "Combine results using appropriate statistical methods so the evidence is pooled correctly and meaningfully. This assists in combining results in the right way with proper methodology." },
  { num: "05", title: "Discuss the Results", desc: "Present findings in straightforward language so the meaning of the numbers is clear and actionable. The findings are given in straightforward language so you know the meaning of the numbers." },
];

const TOOLS = ["R (metafor)", "RevMan 5.4", "Stata 17", "SPSS", "Comprehensive Meta-Analysis", "GRADE Pro", "Rayyan", "Covidence"];

/* ─── ANIMATED COUNTER ───────────────────────────────── */
const Counter = ({ value }) => {
  const [d, setD] = useState("0");
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        const num = parseFloat(value.replace(/[^0-9.]/g, ""));
        const suf = value.replace(/[0-9.]/g, "");
        let s = 0, step = num / 45;
        const t = setInterval(() => {
          s += step;
          if (s >= num) { setD(value); clearInterval(t); }
          else setD(Math.floor(s) + suf);
        }, 28);
        obs.disconnect();
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{d}</span>;
};

/* ─── SERVICE CARD ───────────────────────────────────── */
const ServiceCard = ({ s, i }) => {
  return (
    <div
      style={{
        background: "#fff",
        border: `1.5px solid #e5ecf4`,
        borderRadius: 16,
        padding: "30px 26px 26px",
        cursor: "default",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
         <div style={{ width: 70, height: 70, borderRadius: 14,  display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "background 0.3s" }}>
          {s.image ? (
            <img src={s.image} alt={s.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 24 }}>{s.icon}</span>
          )}
        </div>
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 28, color: "#dbeaf5", lineHeight: 1 }}>{s.num}</span>
      </div>
      <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, fontWeight: 700, color: "#0a2540", margin: "0 0 10px", lineHeight: 1.3, letterSpacing: "-0.01em" }}>{s.title}</h3>
      <p style={{ fontSize: 13, color: "#5a7a90", lineHeight: 1.78, fontFamily: "'Lora', serif", margin: "0 0 18px", flexGrow: 1 }}>{s.desc}</p>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        {s.tags.map(t => (
          <span key={t} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", padding: "3px 9px", borderRadius: 20, background: "#ebf4fb", color: "#1a6faf", fontFamily: "'IBM Plex Mono', monospace" }}>{t}</span>
        ))}
      </div>
    </div>
  );
};

/* ─── MAIN ───────────────────────────────────────────── */
export default function MetaAnalysis() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActiveStep(p => (p + 1) % PROCESS.length), 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <style>{`
       
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --ink: #102712;
          --blue: #2acb35;
          --sky: #6fdd8a;
          --pale: #fff;
          --sand: #fff;
          --muted: #4f6c57;
          --gold: #aacb9e;
        }

        .ma-root {
          font-family: 'Hind', sans-serif;
          background: #fff;
          color: var(--ink);
          overflow-x: hidden;
        }

        /* NAV */
        .ma-nav {
          position: sticky; top: 0; z-index: 100;
          background: rgba(10,37,64,0.96);
          backdrop-filter: blur(16px);
          padding: 0 56px;
          display: flex; align-items: center; justify-content: space-between;
          height: 68px;
          border-bottom: 1px solid rgba(255,255,255,0.07);
        }
        .ma-logo { display: flex; align-items: center; gap: 12px; text-decoration: none; }
        .ma-logo-mark {
          width: 38px; height: 38px; border-radius: 9px;
          background: linear-gradient(135deg, var(--blue), var(--sky));
          display: flex; align-items: center; justify-content: center;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 13px; font-weight: 600; color: #fff; letter-spacing: -0.05em;
        }
        .ma-logo-name { font-family: 'Playfair Display', serif; font-size: 18px; font-weight: 700; color: #fff; letter-spacing: -0.01em; }
        .ma-logo-name span { color: var(--sky); font-style: italic; }
        .ma-nav-links { display: flex; gap: 28px; list-style: none; }
        .ma-nav-links a { text-decoration: none; font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.55); transition: color 0.2s; }
        .ma-nav-links a:hover { color: #fff; }
        .ma-nav-cta {
          background: linear-gradient(135deg, var(--blue), var(--sky));
          color: #fff; border: none;
          padding: 10px 22px; border-radius: 30px;
          font-size: 13px; font-weight: 700;
          cursor: pointer; font-family: 'Hind', sans-serif;
          box-shadow: 0 4px 16px rgba(42,203,53,0.35);
          transition: all 0.2s;
        }
        .ma-nav-cta:hover { opacity: 0.9; transform: translateY(-1px); }
        .ma-hamburger { display: none; background: none; border: none; color: #fff; font-size: 22px; cursor: pointer; }

        /* HERO */
        .ma-hero {
          background: var(--ink);
          position: relative; overflow: hidden;
          padding: 0;
          min-height: 92vh;
          display: grid;
          grid-template-columns: 52% 48%;
        }
        .ma-hero-left {
          padding: 100px 56px 80px;
          display: flex; flex-direction: column; justify-content: center;
          position: relative; z-index: 2;
        }
        .ma-hero-right {
          background: #071624;
          border-left: 1px solid rgba(255,255,255,0.05);
          padding: 60px 48px 60px 40px;
          display: flex; flex-direction: column; justify-content: center;
          position: relative;
          overflow: hidden;
        }
        .ma-hero-right::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 60% 60% at 80% 20%, rgba(42,203,53,0.08) 0%, transparent 70%);
          pointer-events: none;
        }
        .ma-hero::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 40% 60% at 15% 80%, rgba(26,111,175,0.14) 0%, transparent 60%);
          pointer-events: none; z-index: 1;
        }
        .ma-hero-grid {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 30px 30px;
          pointer-events: none; z-index: 0;
        }

        .ma-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(42,203,53,0.12);
          border: 1px solid rgba(42,203,53,0.35);
          color: #7ec8f4;
          font-size: 10px; font-weight: 600;
          letter-spacing: 0.16em; text-transform: uppercase;
          padding: 6px 14px; border-radius: 30px;
          margin-bottom: 28px;
          font-family: 'IBM Plex Mono', monospace;
          width: fit-content;
        }
        .ma-badge-pulse { width: 6px; height: 6px; border-radius: 50%; background: var(--sky); animation: pulse 1.8s ease infinite; }
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.8)} }

        .ma-hero-h1 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(36px, 4.8vw, 68px);
          font-weight: 900;
          color: #fff;
          line-height: 1.07;
          letter-spacing: -0.03em;
          margin-bottom: 24px;
        }
        .ma-hero-h1 em {
          font-style: italic;
          color: var(--sky);
        }
        .ma-hero-sub {
          font-size: 16px; line-height: 1.8;
          color: rgba(255,255,255,0.58);
          font-family: 'Lora', serif;
          margin-bottom: 40px;
          max-width: 440px;
        }
        .ma-hero-btns { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 52px; }
        .btn-p {
          background: linear-gradient(135deg, var(--blue), var(--sky));
          color: #fff; border: none;
          padding: 15px 32px; border-radius: 40px;
          font-size: 14px; font-weight: 700;
          cursor: pointer; font-family: 'Hind', sans-serif;
          box-shadow: 0 8px 28px rgba(42,203,53,0.35);
          transition: all 0.25s; letter-spacing: 0.02em;
        }
        .btn-p:hover { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(74,158,202,0.45); }
        .btn-g {
          background: transparent; color: rgba(255,255,255,0.8);
          border: 1.5px solid rgba(255,255,255,0.18);
          padding: 15px 32px; border-radius: 40px;
          font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: 'Hind', sans-serif;
          transition: all 0.25s;
        }
        .btn-g:hover { background: rgba(255,255,255,0.08); }

        .ma-kws { display: flex; flex-wrap: wrap; gap: 8px; }
        .ma-kw {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 10px; letter-spacing: 0.08em;
          color: rgba(255,255,255,0.3);
          padding: 4px 11px; border-radius: 20px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
        }

        .ma-right-label {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase;
          color: rgba(255,255,255,0.25); margin-bottom: 20px;
        }

        /* STATS */
        .ma-stats {
          background: linear-gradient(135deg, #0a2540 0%, #0d3557 100%);
          display: flex; justify-content: center; flex-wrap: wrap;
          padding: 44px 56px; gap: 0;
        }
        .ma-stat {
          flex: 1; min-width: 160px;
          text-align: center; padding: 16px 24px; position: relative;
        }
        .ma-stat:not(:last-child)::after {
          content: ''; position: absolute;
          right: 0; top: 20%; height: 60%; width: 1px;
          background: rgba(255,255,255,0.08);
        }
        .ma-stat-val {
          display: block;
          font-family: 'Playfair Display', serif;
          font-size: clamp(32px, 4vw, 50px);
          font-weight: 900; color: var(--sky); line-height: 1;
          letter-spacing: -0.02em;
        }
        .ma-stat-lbl {
          display: block; font-size: 11px; font-weight: 600;
          color: rgba(255,255,255,0.45);
          letter-spacing: 0.12em; text-transform: uppercase;
          margin-top: 7px; font-family: 'IBM Plex Mono', monospace;
        }

        /* SERVICES */
        .ma-services { background: var(--sand); padding: 100px 56px; }
        .ma-services-inner { max-width: 1240px; margin: 0 auto; }
        .ma-eyebrow {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 10px; font-weight: 600;
          letter-spacing: 0.22em; text-transform: uppercase;
          color: var(--blue); margin-bottom: 10px;
        }
        .ma-h2 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 3.8vw, 48px);
          font-weight: 700; color: var(--ink);
          letter-spacing: -0.025em; line-height: 1.12;
          margin-bottom: 14px;
        }
        .ma-h2 em { font-style: italic; color: var(--blue); }
        .ma-desc {
          font-size: 15.5px; color: var(--muted);
          line-height: 1.8; font-family: 'Lora', serif;
          max-width: 560px; margin-bottom: 52px;
        }
        .ma-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        /* PROCESS */
        .ma-process { background: var(--ink); padding: 100px 56px; }
        .ma-process-inner { max-width: 1240px; margin: 0 auto; }
        .ma-process-layout {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 72px; align-items: start;
          margin-top: 56px;
        }
        .ma-steps { display: flex; flex-direction: column; gap: 4px; }
        .ma-step {
          display: flex; align-items: flex-start; gap: 18px;
          padding: 20px 24px; border-radius: 14px;
          cursor: pointer;
          transition: all 0.28s;
          border: 1px solid transparent;
        }
        .ma-step.active {
          background: rgba(42,203,53,0.1);
          border-color: rgba(42,203,53,0.25);
        }
        .ma-step-num {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px; font-weight: 600;
          color: var(--sky); letter-spacing: 0.1em;
          flex-shrink: 0; padding-top: 3px;
          transition: color 0.25s;
        }
        .ma-step-title {
          font-family: 'Playfair Display', serif;
          font-size: 16px; font-weight: 700;
          color: rgba(255,255,255,0.85); margin-bottom: 6px;
          transition: color 0.25s;
        }
        .ma-step.active .ma-step-title { color: #fff; }
        .ma-step-desc {
          font-size: 13px; color: rgba(255,255,255,0.4);
          line-height: 1.7; font-family: 'Lora', serif;
          transition: color 0.25s;
        }
        .ma-step.active .ma-step-desc { color: rgba(255,255,255,0.62); }

        /* TOOLS */
        .ma-tools { background: #fff; padding: 72px 56px; border-top: 1px solid #e8f0f7; }
        .ma-tools-inner { max-width: 1240px; margin: 0 auto; text-align: center; }
        .ma-tools-label { font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 28px; }
        .ma-tools-grid { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
        .ma-tool-pill {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px; font-weight: 500;
          padding: 10px 20px; border-radius: 30px;
          background: #f6fcf5;
          color: var(--blue);
          border: 1.5px solid rgba(42,203,53,0.18);
          transition: all 0.2s;
          cursor: default;
        }
        .ma-tool-pill:hover { background: var(--blue); color: #fff; border-color: var(--blue); transform: translateY(-2px); }

        /* CTA */
        .ma-cta {
          background: var(--sand);
          padding: 100px 56px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px; align-items: center;
          max-width: 100%;
        }
        .ma-cta-form {
          background: #fff;
          border: 1.5px solid #dde8f2;
          border-radius: 22px;
          padding: 40px;
          box-shadow: 0 8px 40px rgba(10,37,64,0.07);
        }
        .ma-form-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: var(--ink); margin-bottom: 6px; }
        .ma-form-sub { font-size: 13px; color: var(--muted); font-family: 'Lora', serif; margin-bottom: 26px; }
        .ma-fg { margin-bottom: 14px; }
        .ma-fg label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink); margin-bottom: 5px; font-family: 'IBM Plex Mono', monospace; }
        .ma-fg input, .ma-fg select, .ma-fg textarea {
          width: 100%; padding: 11px 14px;
          border: 1.5px solid #dde8f2; border-radius: 9px;
          font-size: 14px; color: var(--ink);
          font-family: 'Hind', sans-serif;
          background: #f8fbfd; outline: none;
          transition: border-color 0.2s;
        }
        .ma-fg input:focus, .ma-fg select:focus, .ma-fg textarea:focus { border-color: var(--blue); background: #fff; }
        .ma-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .ma-submit {
          width: 100%;
          background: linear-gradient(135deg, var(--blue), var(--sky));
          color: #fff; border: none;
          padding: 14px; border-radius: 11px;
          font-size: 15px; font-weight: 700;
          cursor: pointer; font-family: 'Hind', sans-serif;
          margin-top: 6px;
          box-shadow: 0 6px 24px rgba(74,158,202,0.3);
          transition: all 0.25s; letter-spacing: 0.02em;
        }
        .ma-submit:hover { opacity: 0.9; transform: translateY(-1px); }

        /* FOOTER */
        .ma-footer {
          background: var(--ink);
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 26px 56px;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px;
        }
        .ma-footer p { font-size: 12px; color: rgba(255,255,255,0.3); font-family: 'IBM Plex Mono', monospace; }
        .ma-footer-kws { display: flex; gap: 8px; flex-wrap: wrap; }
        .ma-footer-kw { font-size: 10px; color: rgba(42,203,53,0.75); background: rgba(42,203,53,0.08); padding: 3px 10px; border-radius: 20px; letter-spacing: 0.04em; font-family: 'IBM Plex Mono', monospace; }

        /* RESPONSIVE */
        @media (max-width: 1024px) {
          .ma-hero { grid-template-columns: 1fr; }
          .ma-hero-right { display: none; }
          .ma-process-layout { grid-template-columns: 1fr; }
          .ma-cta { grid-template-columns: 1fr; gap: 48px; }
        }
        @media (max-width: 860px) {
          .ma-nav { padding: 0 24px; }
          .ma-nav-links, .ma-nav-cta { display: none; }
          .ma-hamburger { display: block; }
          .ma-grid { grid-template-columns: repeat(2,1fr); }
        }
        @media (max-width: 580px) {
          .ma-hero-left { padding: 80px 24px 60px; }
          .ma-stats { padding: 32px 20px; }
          .ma-stat:not(:last-child)::after { display: none; }
          .ma-services, .ma-process, .ma-tools, .ma-cta { padding: 60px 24px; }
          .ma-grid { grid-template-columns: 1fr; }
          .ma-form-row { grid-template-columns: 1fr; }
          .ma-footer { padding: 20px 24px; flex-direction: column; align-items: flex-start; }
        }
      `}</style>
<BannerOne
          img={require('../assets/symptoms/meta.png')}
          title={"Meta-Analysis"}
          subtitle={""}
        />
      <div className="ma-root acc-root">
       {/* <PosterOne
        img={aboutbanner}
        title={"Meta - Analysis"}
      /> */}


       

        

        {/* SERVICES */}
        <section className="ma-services">
          <div className="ma-services-inner">
            <p className="ma-eyebrow">What We Offer</p>
            <h2 className="ma-h2">Meta-Analysis Support<br /><em>Built for Reliable Research</em></h2>
            <p className="ma-desc">
              Good studies require more than data gathering—they require clarity of direction, scrutiny, and robust interpretation. We help researchers turn fragmented study findings into a single, dependable conclusion. Our meta-analysis support assists in joining findings of various research and transforming fragmented findings into a single, more powerful outcome. Meta-analysis is a fundamental research subject for clinical science, public pages, publications, and educational materials.
            </p>
            <div className="ma-grid">
              {SERVICES.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
            </div>
          </div>
        </section>

        {/* WHAT IS META-ANALYSIS */}
        <section style={{ background: "#fff", padding: "100px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--blue)", marginBottom: "10px" }}>
              Understanding Meta-Analysis
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.8vw, 48px)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.025em", lineHeight: 1.12, marginBottom: "24px" }}>
              What Is <em style={{ fontStyle: "italic", color: "var(--blue)" }}>Meta-Analysis?</em>
            </h2>
            <div style={{ maxWidth: "920px" }}>
              <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", marginBottom: "20px" }}>
                A meta-analysis is a technique that integrates findings of various studies, helping researchers come to a more solid and trustworthy conclusion. This process consolidates the evidence in a single location as opposed to reading studies individually. It demonstrates treatment effects, compares results, and minimizes confusion due to mixed results, leading to easier and more evidence-based decision-making.
              </p>
              <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", marginBottom: "20px" }}>
                <strong>Why This Matters:</strong> Clinical research usually generates numerous studies on the same subject, but studies can indicate different responses. Some may favor a treatment while others may not. Meta-analysis assists in structuring findings, drawing comparisons, and explaining patterns, leading to more robust medical writing, interpretation, and assured conclusions.
              </p>
            </div>
          </div>
        </section>

        {/* WHY THIS SERVICE MATTERS */}
        <section style={{ background: "var(--sand)", padding: "100px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--blue)", marginBottom: "10px" }}>
              Why This Service Matters
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.8vw, 48px)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.025em", lineHeight: 1.12, marginBottom: "24px" }}>
              Why Researchers Choose This <em style={{ fontStyle: "italic", color: "var(--blue)" }}>Support</em>
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "48px", maxWidth: "1000px" }}>
              <div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", fontWeight: 700, color: "var(--ink)", marginBottom: "12px" }}>Research Challenges</h3>
                <p style={{ fontSize: "15px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif" }}>
                  Most researchers face challenges with time constraints, methodology choices, and interpreting findings. Our expert assistance alleviates this pressure, enhancing accuracy and confidence in projects—particularly crucial in clinical meta-analysis where precision at each phase is essential.
                </p>
              </div>
              <div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", fontWeight: 700, color: "var(--ink)", marginBottom: "12px" }}>Quality & Clarity Focus</h3>
                <p style={{ fontSize: "15px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif" }}>
                  Good research must be both accurate and comprehensible. We emphasize clarity by explaining difficult concepts in easier terms and converting dense insights into actionable intelligence. Quality checks keep projects coherent, essential for theses, dissertations, manuscripts, and evidence summaries.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WHO CAN USE THIS */}
        <section style={{ background: "#fff", padding: "100px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--blue)", marginBottom: "10px" }}>
              Who Can Use This Service
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.8vw, 48px)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.025em", lineHeight: 1.12, marginBottom: "24px" }}>
              Who Can Use This <em style={{ fontStyle: "italic", color: "var(--blue)" }}>Service?</em>
            </h2>
            <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", marginBottom: "32px", maxWidth: "920px" }}>
              This service is customized to suit your research objective and is beneficial for numerous types of clients. Whether you need educational assistance or clinical research support, each project receives individualized attention.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }}>
              {[
                { title: "Medical Students", desc: "Seeking research project guidance and meta-analysis expertise" },
                { title: "Postgraduate Researchers", desc: "Pursuing advanced research with publication aspirations" },
                { title: "PhD Scholars", desc: "Developing dissertation-level evidence synthesis" },
                { title: "Clinical Investigators", desc: "Conducting healthcare research with rigor" },
                { title: "Healthcare Professionals", desc: "Evidence-based practice and clinical decision support" },
                { title: "Academic Authors", desc: "Journal submission and publication-ready research" },
              ].map((client, idx) => (
                <div key={idx} style={{ background: "var(--sand)", borderRadius: "12px", padding: "20px", border: "1px solid #dde8f2" }}>
                  <h4 style={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", fontWeight: 700, color: "var(--ink)", marginBottom: "8px" }}>{client.title}</h4>
                  <p style={{ fontSize: "14px", color: "var(--muted)", lineHeight: 1.6, fontFamily: "'Lora', serif", margin: 0 }}>{client.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT YOU GET */}
        <section style={{ background: "var(--sand)", padding: "100px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--blue)", marginBottom: "10px" }}>
              What Do You Get
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.8vw, 48px)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.025em", lineHeight: 1.12, marginBottom: "24px" }}>
              Core Meta-Analysis Services<br /><em style={{ fontStyle: "italic", color: "var(--blue)" }}>Delivered with Care</em>
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "24px" }}>
              {[
                "Research question planning",
                "Literature screening support",
                "Data extraction guidance",
                "Statistical pooling helps",
                "Forest plot interpretation",
                "Publication-ready reporting",
                "Journal revision support",
              ].map((item, idx) => (
                <div key={idx} style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #dde8f2" }}>
                  <p style={{ fontSize: "15px", color: "var(--ink)", fontWeight: 700, fontFamily: "'Playfair Display', serif", marginBottom: "10px" }}>{idx + 1}. {item}</p>
                  <p style={{ fontSize: "14px", color: "var(--muted)", lineHeight: 1.7, fontFamily: "'Lora', serif", margin: 0 }}>
                    Each step is attended to with care so your research remains clear, rational, and understandable from start to finish.
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY RESEARCHERS CHOOSE US */}
        <section style={{ background: "#fff", padding: "100px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--blue)", marginBottom: "10px" }}>
              Built for Reliable Research
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.8vw, 48px)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.025em", lineHeight: 1.12, marginBottom: "24px" }}>
              Why Researchers Choose This<br /><em style={{ fontStyle: "italic", color: "var(--blue)" }}>Support</em>
            </h2>
            <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", marginBottom: "20px", maxWidth: "920px" }}>
              Researchers face time pressure, method selection, and interpretation challenges. Expert assistance reduces that burden and improves accuracy, especially important in clinical meta-analysis where even minor errors can affect outcomes.
            </p>
            <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", maxWidth: "920px" }}>
              Our service is not just about numbers; it is about guiding the entire process professionally, from planning to reporting, so your project delivers reliable, structured, and usable research outcomes.
            </p>
          </div>
        </section>

        {/* PROCESS */}
        <section className="ma-process">
          <div className="ma-process-inner">
            <p className="ma-eyebrow" style={{ color: "var(--sky)" }}>A Clear Process from Start to Finish</p>
            <h2 className="ma-h2" style={{ color: "#fff" }}>Structured Meta-Analysis<br /><em style={{ color: "var(--sky)" }}>Workflow</em></h2>
            <p className="ma-desc" style={{ color: "rgba(255,255,255,0.5)", marginBottom: 0 }}>
              When the process remains organized, research work becomes simpler. We provide a straightforward and pragmatic five-step workflow that transforms your evidence from raw studies into publication-ready conclusions.
            </p>
            <div className="ma-process-layout">
              <div className="ma-steps">
                {PROCESS.map((p, i) => (
                  <div
                    key={p.num}
                    className={`ma-step ${activeStep === i ? "active" : ""}`}
                    onClick={() => setActiveStep(i)}
                  >
                    <span className="ma-step-num">{p.num}</span>
                    <div>
                      <div className="ma-step-title">{p.title}</div>
                      <div className="ma-step-desc">{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Active step visual */}
              <div style={{ background: "#071624", borderRadius: 20, padding: "40px", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: "0.2em", color: "rgba(255,255,255,0.2)", textTransform: "uppercase", marginBottom: 20 }}>
                  Phase {PROCESS[activeStep].num} · {PROCESS[activeStep].title}
                </div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1.25, marginBottom: 20, letterSpacing: "-0.02em" }}>
                  {PROCESS[activeStep].title}
                </div>
                <p style={{ fontFamily: "'Lora', serif", fontSize: 15, color: "rgba(255,255,255,0.55)", lineHeight: 1.8, marginBottom: 32 }}>
                  {PROCESS[activeStep].desc}
                </p>
                {/* progress bar */}
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 4, height: 4, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${((activeStep + 1) / PROCESS.length) * 100}%`,
                    background: "linear-gradient(90deg, var(--blue), var(--sky))",
                    borderRadius: 4,
                    transition: "width 0.4s ease",
                  }} />
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 8, letterSpacing: "0.1em" }}>
                  Phase {activeStep + 1} of {PROCESS.length}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLINICAL SUPPORT */}
        <section style={{ background: "var(--sand)", padding: "100px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--blue)", marginBottom: "10px" }}>
              How We Help
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.8vw, 48px)", fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.025em", lineHeight: 1.12, marginBottom: "24px" }}>
              Support for Better Clinical <em style={{ fontStyle: "italic", color: "var(--blue)" }}>Decisions</em>
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "48px", alignItems: "center", maxWidth: "1200px" }}>
              <div>
                <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", marginBottom: "16px" }}>
                  Sound evidence should guide clinical decisions. Our service assists in turning individual studies into meaningful orientation that aids comparison of treatments, review of outcomes, and systematic discussion of evidence.
                </p>
                <p style={{ fontSize: "16px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", marginBottom: "16px" }}>
                  Many clients seek meta-analysis services when ready to:
                </p>
                <ul style={{ fontSize: "15px", color: "var(--muted)", lineHeight: 1.8, fontFamily: "'Lora', serif", margin: "0 0 16px 20px" }}>
                  <li>Publish research findings</li>
                  <li>Submit work to academic institutions</li>
                  <li>Undergo specialist review</li>
                  <li>Enhance research value and credibility</li>
                </ul>
              </div>
              <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", border: "1px solid #dde8f2" }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "22px", fontWeight: 700, color: "var(--ink)", marginBottom: "16px" }}>Key Benefits</h3>
                {[
                  "Minimize confusion in research",
                  "Reliable structure and methodology",
                  "Improved accuracy and confidence",
                  "Publication-ready outcomes",
                  "Expert guidance throughout",
                  "Focus on quality and clarity",
                ].map((benefit, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
                    <span style={{ color: "var(--blue)", fontSize: "20px", lineHeight: 1, marginTop: "2px" }}>✓</span>
                    <span style={{ fontSize: "15px", color: "var(--muted)", lineHeight: 1.6, fontFamily: "'Lora', serif" }}>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* TOOLS */}
        {/* <section className="ma-tools">
          <div className="ma-tools-inner">
            <p className="ma-tools-label">Statistical Tools & Software We Use</p>
            <div className="ma-tools-grid">
              {TOOLS.map(t => <span className="ma-tool-pill" key={t}>{t}</span>)}
            </div>
          </div>
        </section>
        */}
 <section className="service-cta">
          <div className="service-container">
            <h2>Your Research Deserves Precision and Clarity</h2>
            <p> Connect with us today to discuss your meta-analysis project needs. We will be reviewing your research question, your existing studies, and the stage your project is at, and guide you toward evidence synthesis that is structured, accurate, and publication-ready.</p>
            <div className="service-btns" style={{ justifyContent: 'center' }}>
              <button className="service-btn-primary" onClick={() => navigate('/consultations')}>Get Started</button>
            </div>
          </div>
        </section>
       

        
      </div>
    </>
  );
}